import { ReactNode, useState } from "react"
import { Power, PowerOff } from "lucide-react"
import { AdminLocationForm, AdminLocationFormValue, StatusOption } from "@/features/admin/location-form"
import { TimeZoneOption } from "@/lib/time-zone"
import { AdminLayout } from "@/components/layouts/admin-layout"
import { Badge } from "@/components/ui/badge"
import { Card, CardAction, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { MainContainer } from "@/components/ui/container"
import { PageHeader } from "@/components/ui/headers"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { formatDateTime } from "@/lib/formatters"
import { t } from "@/lib/i18n"
import { patchInline, toastInlineUpdateError, toastInlineUpdateSuccess } from "@/lib/inline-update"

/**
 * The upper tier of the two-tier model: what this location is *allowed* to use.
 * `enabled` rides along read-only — revoking something the restaurant is actively
 * using is a different decision from revoking something dormant, and the operator
 * making the call deserves to see which one this is.
 */
type LocationFeature = {
  key: string
  label: string
  description: string
  available: boolean
  enabled: boolean
}

/**
 * The catalog arrives already split and already ordered. Grouping client-side
 * would mean keeping a copy of the section order in TypeScript, and the next
 * feature added to `config/location_features.yml` would land in whichever
 * section that stale copy happened to list first.
 */
type FeatureSection = {
  group: string
  label: string
  features: LocationFeature[]
}

/**
 * Identity and provenance, not settings — everything editable lives in the form
 * below it. Serialized by `Admin::LocationSerializer`'s `admin_details` view.
 */
type LocationDetails = {
  id: number
  status: string
  statusLabel: string
  timeZone: string | null
  address: string | null
  organizationName: string | null
  createdAt: string
  updatedAt: string
}

type Props = {
  location: AdminLocationFormValue
  statusOptions: StatusOption[]
  timezones: TimeZoneOption[]
  features?: FeatureSection[]
  details?: LocationDetails | null
  errors?: Record<string, string | string[]>
}

const tEdit = (key: string, options: Record<string, string | number> = {}) =>
  t(`ui.react.pages.admin.locations.edit.${key}`, options)

function statusVariant(status: string) {
  if (status === "suspended") return "destructive"
  if (status === "active") return "default"

  return "outline"
}

/** One fact per row, matching the admin transaction detail screen. */
function Fact({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 border-b border-border py-2 last:border-b-0 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-sm tabular-nums text-foreground">{value || "—"}</dd>
    </div>
  )
}

function LocationFacts({ details }: { details: LocationDetails }) {
  // Timestamps read in the location's own zone: "created at 9pm" means nothing
  // to an operator if the 9pm belongs to the browser rather than the restaurant.
  const timeZone = details.timeZone ?? undefined

  return (
    <Card density="compact" data-testid="admin-location-details">
      <CardHeader>
        <CardTitle className="text-sm">{tEdit("details_title")}</CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="grid gap-x-8 md:grid-cols-2">
          <Fact label={tEdit("detail_id")} value={`#${details.id}`} />
          <Fact
            label={tEdit("detail_status")}
            value={<Badge variant={statusVariant(details.status)}>{details.statusLabel}</Badge>}
          />
          <Fact label={tEdit("detail_time_zone")} value={details.timeZone} />
          <Fact label={tEdit("detail_organization")} value={details.organizationName} />
          <Fact label={tEdit("detail_address")} value={details.address} />
          <Fact
            label={tEdit("detail_created_at")}
            value={formatDateTime(details.createdAt, { timeZone })}
          />
          <Fact
            label={tEdit("detail_updated_at")}
            value={formatDateTime(details.updatedAt, { timeZone })}
          />
        </dl>
      </CardContent>
    </Card>
  )
}

/**
 * Whether the restaurant is actually running a feature admin granted. It used to
 * be a line of prose under the description, which competed with the feature's own
 * description for the same reading; a mark beside the switch answers it at a
 * glance instead. The sentence survives as the tooltip and as the accessible
 * name, so nothing that could read the prose loses it.
 *
 * The trigger is a plain span rather than a focusable control: the text is
 * already in the accessibility tree, so a tab stop per feature would buy nothing
 * and cost twenty of them on this screen.
 */
function FeatureUsage({ feature }: { feature: LocationFeature }) {
  const label = feature.enabled ? tEdit("feature_in_use") : tEdit("feature_not_in_use")
  const Icon = feature.enabled ? Power : PowerOff

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={feature.enabled ? "text-success" : "text-muted-foreground"}
          data-testid={`admin-location-feature-${feature.key}-usage`}
          data-state={feature.enabled ? "in-use" : "idle"}
        >
          <Icon className="size-4" aria-hidden="true" />
          <span className="sr-only">{label}</span>
        </span>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  )
}

function FeatureAvailability({ locationId, sections }: { locationId: number; sections: FeatureSection[] }) {
  const [pendingKey, setPendingKey] = useState<string | null>(null)

  const toggle = (key: string, available: boolean) => {
    setPendingKey(key)
    patchInline(`/locations/${locationId}/features/${key}`, { available })
      .then((result) => toastInlineUpdateSuccess(result))
      .catch((error: unknown) => toastInlineUpdateError(error))
      .finally(() => setPendingKey(null))
  }

  return (
    <TooltipProvider>
      <div className="space-y-6" data-testid="admin-location-features">
        {sections.map((section) => {
          const granted = section.features.filter((feature) => feature.available).length

          return (
            <section key={section.group} className="space-y-2">
              {/* The count is what makes twenty switches scannable: it answers
                  "what does this restaurant have here" without reading each card. */}
              <div className="flex items-baseline justify-between gap-2">
                <h3 className="truncate text-sm font-semibold tracking-tight">{section.label}</h3>
                <span
                  className="shrink-0 text-xs tabular-nums text-muted-foreground"
                  data-testid={`admin-location-feature-group-${section.group}-count`}
                >
                  {tEdit("features_granted_count", { granted, total: section.features.length })}
                </span>
              </div>

              {/* Cards wrap across the row and grow into the slack, capped so a
                  section with an odd count does not end in one banner-wide card. */}
              <div className="flex flex-wrap gap-4">
                {section.features.map((feature) => (
                  <Card
                    key={feature.key}
                    density="compact"
                    className="min-w-0 max-w-md flex-1 basis-72"
                    data-testid={`admin-location-feature-${feature.key}`}
                  >
                    <CardHeader>
                      <CardTitle className="text-sm">
                        <Label htmlFor={`admin-feature-${feature.key}`} className="text-sm font-medium">
                          {feature.label}
                        </Label>
                      </CardTitle>
                      <CardAction className="flex items-center gap-2">
                        {feature.available && <FeatureUsage feature={feature} />}
                        <Switch
                          id={`admin-feature-${feature.key}`}
                          checked={feature.available}
                          disabled={pendingKey === feature.key}
                          onCheckedChange={(next) => toggle(feature.key, next)}
                          data-testid={`admin-location-feature-${feature.key}-switch`}
                        />
                      </CardAction>
                    </CardHeader>
                    <CardContent>
                      <p className="text-xs text-muted-foreground">{feature.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>
          )
        })}
      </div>
    </TooltipProvider>
  )
}

// `features` and `details` default to absent rather than being assumed present:
// the "new location" screen shares this form and has no record to describe or to
// grant anything against, and the update failure path re-renders with whatever
// the operator typed.
export default function AdminLocationsEdit({
  location,
  statusOptions,
  timezones,
  features = [],
  details,
  errors,
}: Props) {
  const locationId = location.id
  const showFeatures = locationId != null && features.length > 0

  return (
    <AdminLayout>
      <MainContainer size="md">
        <PageHeader title={location.name || tEdit("title")} description={tEdit("description")} />

        <Tabs defaultValue="details" className="mt-4">
          <TabsList>
            <TabsTrigger value="details" data-testid="admin-location-tab-details">
              {tEdit("tab_details")}
            </TabsTrigger>
            {showFeatures && (
              <TabsTrigger value="features" data-testid="admin-location-tab-features">
                {tEdit("tab_features")}
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="details" className="space-y-4">
            {details && <LocationFacts details={details} />}

            <AdminLocationForm
              location={location}
              statusOptions={statusOptions}
              timezones={timezones}
              action={`/locations/${location.id}`}
              method="patch"
              title={tEdit("form_title")}
              errors={errors}
            />
          </TabsContent>

          {showFeatures && (
            <TabsContent value="features" className="space-y-4">
              {/* The two-tier model is not self-evident from a row of switches:
                  granting is not turning on, and the restaurant owns the second
                  decision. */}
              <p className="text-sm text-muted-foreground">{tEdit("features_description")}</p>

              <FeatureAvailability locationId={locationId} sections={features} />
            </TabsContent>
          )}
        </Tabs>
      </MainContainer>
    </AdminLayout>
  )
}
